import React, { useState } from "react";

import {
  ArrayUtils,
  isPercentageComplete,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingBeefFootprint,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprint,
  useGetFootprintingFarmFootprint,
  useGetFootprintingPigletFootprint,
  useGetFootprintingPorkFootprint,
  useGetPracticeCrops,
  useMutateFootprintingBeefPrimaryData,
  useMutateFootprintingCropCreateManyBlankPrimaryData,
  useMutateFootprintingDairyPrimaryData,
  useMutateFootprintingFarmPrimaryData,
  useMutateFootprintingPigletPrimaryData,
  useMutateFootprintingPorkPrimaryData,
  useTranslation,
} from "utils";

import { AlertBanner, Button, Flex, Icon, SelectableImageCard, Typography } from "components";

import { FootprintingAssessmentAutofill, FootprintingAssessmentAutofillChoice } from "pages/app/footprinting/components/FootprintingAssessmentAutofill";
import { CARD_TYPE_ICON, FootprintingLoadingState } from "pages/app/footprinting/components/FootprintingFormButton";

export interface AssessmentSelectionScreenProps {
  year: number;
  farmId: string;
  onDidAutofill?: () => void;
}

export const AssessmentSelectionScreen = ({ year, farmId, onDidAutofill }: AssessmentSelectionScreenProps) => {
  const { t } = useTranslation("footprinting");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCommodities, setSelectedCommodities] = useState<Record<string, boolean>>({});
  const [selectedBeef, setSelectedBeef] = useState(false);
  const [selectedDairy, setSelectedDairy] = useState(false);
  const [selectedPiglet, setSelectedPiglet] = useState(false);
  const [selectedPork, setSelectedPork] = useState(false);
  const [showAutofillPage, setShowAutofillPage] = useState(false);
  const [autofillChoice, setAutofillChoice] = useState<FootprintingAssessmentAutofillChoice>("autofill");
  const practiceCropsQuery = useGetPracticeCrops({ enabled: true });
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);

  const { mutateAsync: createBlankFarmPrimaryDataRow } = useMutateFootprintingFarmPrimaryData(farmId, year, { farmId, year }, "");
  const { mutateAsync: createBlankBeefPrimaryDataRow } = useMutateFootprintingBeefPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankDairyPrimaryDataRow } = useMutateFootprintingDairyPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankPigletPrimaryDataRow } = useMutateFootprintingPigletPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankPorkPrimaryDataRow } = useMutateFootprintingPorkPrimaryData(farmId, year, { farmId, year });
  const { mutateAsync: createBlankCropPrimaryDataRows } = useMutateFootprintingCropCreateManyBlankPrimaryData(
    farmId,
    carbonProgramQuery.data?.farmLevelFootprinting?.programCrops ?? [],
    year
  );

  const selectedCropIds = Object.entries(selectedCommodities)
    .filter(([_, isSelected]) => isSelected)
    .map(([cropId]) => cropId);

  const previousYearFarmQuery = useGetFootprintingFarmFootprint(farmId, year - 1, { enabled: showAutofillPage });
  const previousYearCropsQueries = useGetFootprintingCropFootprints(farmId, [year - 1], selectedCropIds, { enabled: showAutofillPage });
  const previousYearBeefQuery = useGetFootprintingBeefFootprint(farmId, year - 1, {
    enabled: showAutofillPage && carbonProgramQuery.data?.platformFeatures.footprinting?.enabled,
  });
  const previousYearDairyQuery = useGetFootprintingDairyFootprint(farmId, year - 1, {
    enabled: showAutofillPage && carbonProgramQuery.data?.platformFeatures.footprinting?.dairy,
  });
  const previousYearPorkQuery = useGetFootprintingPorkFootprint(farmId, year - 1, {
    enabled: showAutofillPage && carbonProgramQuery.data?.platformFeatures.footprinting?.pork,
  });
  const previousYearPigletQuery = useGetFootprintingPigletFootprint(farmId, year - 1, {
    enabled: showAutofillPage && carbonProgramQuery.data?.platformFeatures.footprinting?.pork,
  });

  const queriesAreLoading = [
    carbonProgramQuery,
    practiceCropsQuery,
    previousYearFarmQuery,
    ...previousYearCropsQueries,
    previousYearBeefQuery,
    previousYearDairyQuery,
    previousYearPorkQuery,
    previousYearPigletQuery,
  ].some(queryIsLoading);

  if (queriesAreLoading) return <FootprintingLoadingState cardType={"crop"} />;
  if (
    !carbonProgramQuery.data?.farmLevelFootprinting?.programCrops &&
    carbonProgramQuery.data?.platformFeatures.footprinting?.beef !== true &&
    carbonProgramQuery.data?.platformFeatures.footprinting?.dairy !== true &&
    carbonProgramQuery.data?.platformFeatures.footprinting?.pork !== true
  )
    return null;

  const autofillableItems = [
    ...(isPercentageComplete(previousYearFarmQuery.data?.completionPct ?? 0) ? [t("assessmentSelection.autofillChoice.prefillFarmOperations")] : []),
    ...(selectedBeef && isPercentageComplete(previousYearBeefQuery.data?.completionPct ?? 0) ? [t("assessmentSelection.beef")] : []),
    ...previousYearCropsQueries
      .filter((pycq) => isPercentageComplete(pycq.data?.completionPct ?? 0) && selectedCropIds.some((cid) => cid === pycq.data?.cropId))
      .map((pycq) => practiceCropsQuery.data?.find((crop) => crop.id === pycq.data?.cropId)?.name)
      .filter((cropName): cropName is string => !!cropName),
    ...(selectedDairy && isPercentageComplete(previousYearDairyQuery.data?.completionPct ?? 0) ? [t("assessmentSelection.dairy")] : []),
    ...(selectedPork && isPercentageComplete(previousYearPorkQuery.data?.completionPct ?? 0) ? [t("assessmentSelection.pork")] : []),
    ...(selectedPiglet && isPercentageComplete(previousYearPigletQuery.data?.completionPct ?? 0) ? [t("assessmentSelection.piglet")] : []),
  ];

  const onClickFinish = () => {
    setIsLoading(true);
    const autofillYear =
      autofillableItems.length === 0 || autofillChoice === "no-autofill" || year === carbonProgramQuery.data?.farmLevelFootprinting?.startYear
        ? undefined
        : year - 1;

    const [cropIdsToAutofill, cropIdsNoAutofill] = ArrayUtils.partition(selectedCropIds, (scid) =>
      previousYearCropsQueries.some((pycq) => pycq.data?.cropId === scid && isPercentageComplete(pycq.data.completionPct ?? 0))
    );

    Promise.all([
      createBlankFarmPrimaryDataRow({
        autofillYear,
      }),
      createBlankCropPrimaryDataRows({
        selectedCropIds: cropIdsToAutofill,
        autofillYear,
      }),
      createBlankCropPrimaryDataRows({
        selectedCropIds: cropIdsNoAutofill,
      }),
      ...(selectedBeef ? [createBlankBeefPrimaryDataRow({ autofillYear })] : []),
      ...(selectedDairy ? [createBlankDairyPrimaryDataRow({ autofillYear })] : []),
      ...(selectedPiglet ? [createBlankPigletPrimaryDataRow({ autofillYear })] : []),
      ...(selectedPork ? [createBlankPorkPrimaryDataRow({ autofillYear })] : []),
    ]).then(() => (autofillYear !== undefined && onDidAutofill ? onDidAutofill() : null));
  };

  const onClickNext = () => {
    if (year === carbonProgramQuery.data?.farmLevelFootprinting?.startYear || autofillableItems.length === 0) {
      return onClickFinish();
    }

    setShowAutofillPage(true);
  };

  if (showAutofillPage) {
    return (
      <FootprintingAssessmentAutofill
        year={year - 1}
        selection={autofillChoice}
        itemsToAutofill={autofillableItems}
        onChangeSelection={setAutofillChoice}
        onBack={() => setShowAutofillPage(false)}
        onContinue={onClickFinish}
      />
    );
  }

  const hasAnyAssessmentSelected = selectedCropIds.length > 0 || selectedBeef || selectedDairy || selectedPiglet || selectedPork;
  const selectableCrops = (carbonProgramQuery.data.farmLevelFootprinting?.programCrops ?? [])
    .map((cropId) => {
      const cropInfo = practiceCropsQuery.data?.find((c) => c.id === cropId);
      if (!cropInfo) return null;

      return {
        cropId,
        cropName: cropInfo.name,
      };
    })
    .filter((item) => item !== null)
    .sort((a, b) => a.cropName.localeCompare(b.cropName));

  return (
    <Flex vertical className="padding-top-200" gap="2rem">
      <Flex className="assessment-selection" vertical align="stretch" gap="2rem">
        <Flex className="assessment-selection-text" vertical gap={0} align="center">
          <Typography variant="paragraph-m" color="grey-600">
            {t("assessmentSelection.callout")}
          </Typography>
          <Typography variant="h3">{t("assessmentSelection.title")}</Typography>
        </Flex>
        <Flex wrap="wrap" gap="1rem" flex={1} justify="center">
          {selectableCrops.map(({ cropId, cropName }) => (
            <Flex className="flex-column-item-3-wide" key={cropId}>
              <SelectableImageCard
                selectionMode="multiple"
                title={cropName}
                titleVariant="paragraph-m"
                checked={selectedCommodities[cropId]}
                onChange={() => setSelectedCommodities({ ...selectedCommodities, [cropId]: !selectedCommodities[cropId] })}
                icon={{
                  iconAlt: `${cropName} icon`,
                  iconName: CARD_TYPE_ICON.crop,
                }}
              />
            </Flex>
          ))}
          {carbonProgramQuery.data.platformFeatures.footprinting?.beef === true ? (
            <Flex className="flex-column-item-3-wide" key="beef">
              <SelectableImageCard
                selectionMode="multiple"
                title={t("assessmentSelection.beef")}
                titleVariant="paragraph-m"
                checked={selectedBeef}
                onChange={(e) => setSelectedBeef(!!e.target?.checked)}
                icon={{
                  iconAlt: "Beef icon",
                  iconName: CARD_TYPE_ICON.beef,
                }}
              />
            </Flex>
          ) : null}
          {carbonProgramQuery.data?.platformFeatures?.footprinting?.dairy === true ? (
            <Flex className="flex-column-item-3-wide" key="dairy">
              <SelectableImageCard
                selectionMode="multiple"
                title={t("assessmentSelection.dairy")}
                titleVariant="paragraph-m"
                checked={selectedDairy}
                onChange={(e) => setSelectedDairy(!!e.target?.checked)}
                icon={{
                  iconAlt: "Dairy icon",
                  iconName: CARD_TYPE_ICON.dairy,
                }}
              />
            </Flex>
          ) : null}
          {carbonProgramQuery.data?.platformFeatures?.footprinting?.pork === true ? (
            <Flex className="flex-column-item-3-wide" key="piglet">
              <SelectableImageCard
                selectionMode="multiple"
                title={t("assessmentSelection.piglet")}
                titleVariant="paragraph-m"
                checked={selectedPiglet}
                onChange={(e) => setSelectedPiglet(!!e.target?.checked)}
                icon={{
                  iconAlt: "Piglet icon",
                  iconName: CARD_TYPE_ICON.piglet,
                }}
              />
            </Flex>
          ) : null}
          {carbonProgramQuery.data?.platformFeatures?.footprinting?.pork === true ? (
            <Flex className="flex-column-item-3-wide" key="pork">
              <SelectableImageCard
                selectionMode="multiple"
                title={t("assessmentSelection.pork")}
                titleVariant="paragraph-m"
                checked={selectedPork}
                onChange={(e) => setSelectedPork(!!e.target?.checked)}
                icon={{
                  iconAlt: "Pork icon",
                  iconName: CARD_TYPE_ICON.pork,
                }}
              />
            </Flex>
          ) : null}
        </Flex>
        {autofillableItems.length > 0 ? (
          <AlertBanner
            headingSize="h6"
            gap={12}
            title={t("assessmentSelection.banner.title")}
            description={t("assessmentSelection.banner.description")}
            iconName="tabler/bulb"
            iconSize={24}
            type="recommended"
            iconAlign="start"
          />
        ) : null}
        <Button className="assessment-selection-next-button" type="primary" disabled={isLoading || !hasAnyAssessmentSelected} onClick={onClickNext}>
          {t("assessmentSelection.nextButton")}
        </Button>
      </Flex>
      <Flex justify="space-between" align="center">
        <Typography variant="paragraph-m" color="grey-600" className="flex-center-align icon-row">
          <Icon iconName="tabler/circle-check" /> {t("assessmentSelection.footerItems.insights")}
        </Typography>
        <Typography variant="paragraph-m" color="grey-600" className="flex-center-align icon-row">
          <Icon iconName="tabler/circle-check" /> {t("assessmentSelection.footerItems.benefits")}
        </Typography>
        <Typography variant="paragraph-m" color="grey-600" className="flex-center-align icon-row">
          <Icon iconName="tabler/circle-check" /> {t("assessmentSelection.footerItems.payout")}
        </Typography>
      </Flex>
    </Flex>
  );
};
